import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, deleteDoc, doc, updateDoc, addDoc, query, where } from 'firebase/firestore';
import { auth, db, functions } from '@/firebase';
import { httpsCallable } from 'firebase/functions';

interface Quiz {
    id: string;
    Quiz: string;
    CollectAnswer: string;
    Explanation?: string;
    Chapter: number;
    Qno: number;
    SKref: any; // Reference
    SKrefName?: string;
    UID?: string;
    sync?: string;
}

interface Shikaku {
    id: string;
    ShikakuName: string;
}

interface SyncResult {
    success: boolean;
    results: {
        created: number;
        updated: number;
        errors: number;
        errorDetails: string[];
    };
}

export default function QuizzesManagement() {
    const navigate = useNavigate();
    const [quizzes, setQuizzes] = useState<Quiz[]>([]);
    const [shikakuList, setShikakuList] = useState<Shikaku[]>([]);
    const [selectedShikaku, setSelectedShikaku] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [editingQuiz, setEditingQuiz] = useState<Quiz | null>(null);
    const [isAdding, setIsAdding] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState<SyncResult | null>(null);

    // Form state
    const [form, setForm] = useState<Partial<Quiz>>({
        Quiz: '',
        CollectAnswer: '',
        Explanation: '',
        Chapter: 1,
        Qno: 1,
        SKref: null
    });
    const [selectedShikakuForForm, setSelectedShikakuForForm] = useState<string>('');

    useEffect(() => {
        fetchShikaku();
    }, []);

    useEffect(() => {
        if (selectedShikaku) {
            fetchQuizzes(selectedShikaku);
        } else {
            setQuizzes([]);
            setLoading(false);
        }
    }, [selectedShikaku]);

    const fetchShikaku = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, 'Shikaku'));
            const shikakuData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ShikakuName: doc.data().ShikakuName || ''
            })) as Shikaku[];
            setShikakuList(shikakuData);
            if (shikakuData.length > 0) {
                setSelectedShikaku(shikakuData[0].id);
            }
        } catch (error) {
            console.error('Error fetching Shikaku:', error);
        }
    };

    const fetchQuizzes = async (shikakuId: string) => {
        setLoading(true);
        try {
            const shikakuRef = doc(db, 'Shikaku', shikakuId);
            const q = query(collection(db, 'Quiz'), where('SKref', '==', shikakuRef));
            const querySnapshot = await getDocs(q);

            const quizzesData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Quiz[];

            // Sort by Chapter then Qno
            quizzesData.sort((a, b) => {
                if (a.Chapter !== b.Chapter) return a.Chapter - b.Chapter;
                return a.Qno - b.Qno;
            });

            setQuizzes(quizzesData);
        } catch (error: any) {
            console.error('Error fetching quizzes:', error);
            const currentUser = auth.currentUser;
            const debugInfo = `
Error Code: ${error.code}
Error Message: ${error.message}
User ID: ${currentUser?.uid}
Email: ${currentUser?.email}
Time: ${new Date().toLocaleString()}
            `;
            alert(`クイズの取得に失敗しました:\n${debugInfo}`);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (quiz: Quiz) => {
        setEditingQuiz(quiz);
        setForm({ ...quiz });
        if (quiz.SKref && quiz.SKref.id) {
            setSelectedShikakuForForm(quiz.SKref.id);
        } else if (selectedShikaku) {
            setSelectedShikakuForForm(selectedShikaku);
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('このクイズを削除してもよろしいですか？')) return;

        try {
            await deleteDoc(doc(db, 'Quiz', id));
            alert('クイズを削除しました');
            fetchQuizzes(selectedShikaku);
        } catch (error: any) {
            console.error('Error deleting quiz:', error);
            const currentUser = auth.currentUser;
            const debugInfo = `
Error Code: ${error.code}
Error Message: ${error.message}
User ID: ${currentUser?.uid}
Email: ${currentUser?.email}
Time: ${new Date().toLocaleString()}
            `;
            alert(`削除に失敗しました:\n${debugInfo}`);
        }
    };

    const handleSave = async () => {
        if (!form.Quiz || !form.CollectAnswer || !selectedShikakuForForm) {
            alert('必須項目を入力してください');
            return;
        }

        try {
            const shikakuRef = doc(db, 'Shikaku', selectedShikakuForForm);
            const saveData = {
                ...form,
                SKref: shikakuRef,
                Chapter: Number(form.Chapter),
                Qno: Number(form.Qno)
            };

            if (editingQuiz) {
                await updateDoc(doc(db, 'Quiz', editingQuiz.id), saveData);
                alert('クイズを更新しました');
            } else {
                await addDoc(collection(db, 'Quiz'), saveData);
                alert('クイズを追加しました');
            }

            setEditingQuiz(null);
            setIsAdding(false);
            setForm({
                Quiz: '',
                CollectAnswer: '',
                Explanation: '',
                Chapter: 1,
                Qno: 1
            });
            fetchQuizzes(selectedShikaku);
        } catch (error: any) {
            console.error('Error saving quiz:', error);
            const currentUser = auth.currentUser;
            const debugInfo = `
Error Code: ${error.code}
Error Message: ${error.message}
User ID: ${currentUser?.uid}
Email: ${currentUser?.email}
Time: ${new Date().toLocaleString()}
            `;
            alert(`保存に失敗しました:\n${debugInfo}`);
        }
    };

    const openAddModal = () => {
        setIsAdding(true);
        setForm({
            Quiz: '',
            CollectAnswer: '',
            Explanation: '',
            Chapter: 1,
            Qno: 1
        });
        setSelectedShikakuForForm(selectedShikaku);
    };

    const handleLogout = async () => {
        try {
            await auth.signOut();
            navigate('/login');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const handleDeleteAll = async () => {
        if (!selectedShikaku) {
            alert('資格を選択してください');
            return;
        }

        const shikaku = shikakuList.find(s => s.id === selectedShikaku);
        const confirmMsg = `【警告】\n「${shikaku?.ShikakuName}」の全クイズ (${quizzes.length}件) を削除します。\n\nこの操作は取り消せません。\n本当に削除しますか?`;

        if (!confirm(confirmMsg)) return;
        if (!confirm('本当によろしいですか? この操作は取り消せません。')) return;

        setLoading(true);
        try {
            let deletedCount = 0;
            for (const quiz of quizzes) {
                await deleteDoc(doc(db, 'Quiz', quiz.id));
                deletedCount++;
            }
            alert(`${deletedCount}件のクイズを削除しました`);

            if (selectedShikaku) {
                fetchQuizzes(selectedShikaku);
            }
        } catch (error: any) {
            console.error('Delete error:', error);
            alert(`削除に失敗しました: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleSync = async () => {
        if (!confirm('スプレッドシートからデータを同期しますか?\n既存のデータは上書きされます。')) return;

        setIsSyncing(true);
        try {
            const syncFunction = httpsCallable(functions, 'syncQuizzesFromSheet');
            const result = await syncFunction();

            const data = result.data as SyncResult;
            setSyncResult(data);
            alert(`同期完了!\n新規作成: ${data.results.created}件\n更新: ${data.results.updated}件\nエラー: ${data.results.errors}件`);

            if (selectedShikaku) {
                fetchQuizzes(selectedShikaku);
            }
        } catch (error: any) {
            console.error('Sync error:', error);
            alert(`同期に失敗しました: ${error.message}`);
        } finally {
            setIsSyncing(false);
        }
    };

    return (
        <div className="">
            <div className="max-w-7xl mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800 mb-2">クイズ管理</h1>
                        <p className="text-gray-600">資格試験のクイズを管理します</p>
                    </div>
                    <div className="flex gap-4">
                        <button
                            onClick={handleSync}
                            disabled={isSyncing}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isSyncing ? (
                                <>
                                    <span className="animate-spin">⟳</span>
                                    同期中...
                                </>
                            ) : (
                                '📊 スプレッドシートから同期'
                            )}
                        </button>
                        <button
                            onClick={openAddModal}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                            + 新規作成
                        </button>
                    </div>
                </div>

                {/* Shikaku Selection */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        資格を選択
                    </label>
                    <select
                        value={selectedShikaku}
                        onChange={(e) => setSelectedShikaku(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">-- 選択してください --</option>
                        {shikakuList.map(shikaku => (
                            <option key={shikaku.id} value={shikaku.id}>
                                {shikaku.ShikakuName}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Edit Modal */}
                {(isAdding || editingQuiz) && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                            <h2 className="text-2xl font-bold mb-6">
                                {editingQuiz ? 'クイズを編集' : '新しいクイズを作成'}
                            </h2>

                            <div className="space-y-4">
                                {/* Chapter */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Chapter <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={form.Chapter || 1}
                                        onChange={(e) => setForm({ ...form, Chapter: parseInt(e.target.value) })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                {/* Qno */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        問題番号 <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={form.Qno || 1}
                                        onChange={(e) => setForm({ ...form, Qno: parseInt(e.target.value) })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                {/* Quiz */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        クイズ（問題文） <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        value={form.Quiz || ''}
                                        onChange={(e) => setForm({ ...form, Quiz: e.target.value })}
                                        rows={3}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                {/* CollectAnswer */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        正解 <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={form.CollectAnswer || ''}
                                        onChange={(e) => setForm({ ...form, CollectAnswer: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="例: A, B, ○, など"
                                    />
                                </div>

                                {/* Explanation */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        解説
                                    </label>
                                    <textarea
                                        value={form.Explanation || ''}
                                        onChange={(e) => setForm({ ...form, Explanation: e.target.value })}
                                        rows={3}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                {/* Shikaku Selection in Form */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        資格 <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={selectedShikakuForForm}
                                        onChange={(e) => setSelectedShikakuForForm(e.target.value)}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">-- 選択してください --</option>
                                        {shikakuList.map(shikaku => (
                                            <option key={shikaku.id} value={shikaku.id}>
                                                {shikaku.ShikakuName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Modal Buttons */}
                            <div className="flex gap-4 mt-8">
                                <button
                                    onClick={handleSave}
                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    保存
                                </button>
                                <button
                                    onClick={() => {
                                        setIsAdding(false);
                                        setEditingQuiz(null);
                                    }}
                                    className="flex-1 px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-colors"
                                >
                                    キャンセル
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Quizzes Table */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    {loading ? (
                        <div className="text-center py-8">読み込み中...</div>
                    ) : quizzes.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            クイズがありません
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-100 border-b">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Chapter</th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Qno</th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">クイズ</th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">正解</th>
                                        <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700">操作</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {quizzes.map((quiz) => (
                                        <tr key={quiz.id} className="border-b hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 text-sm text-gray-800">{quiz.Chapter}</td>
                                            <td className="px-6 py-4 text-sm text-gray-800">{quiz.Qno}</td>
                                            <td className="px-6 py-4 text-sm text-gray-800">
                                                <div className="max-w-xs truncate" title={quiz.Quiz}>
                                                    {quiz.Quiz}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-800">{quiz.CollectAnswer}</td>
                                            <td className="px-6 py-4 text-sm text-center">
                                                <button
                                                    onClick={() => handleEdit(quiz)}
                                                    className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 mr-2"
                                                >
                                                    編集
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(quiz.id)}
                                                    className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                                                >
                                                    削除
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Delete All Button */}
                {!loading && quizzes.length > 0 && (
                    <div className="mt-6">
                        <button
                            onClick={handleDeleteAll}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                            🗑️ すべてのクイズを削除
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
